import { useEffect, useState } from 'react';

import { useSidebar } from '../../../contexts/SidebarContext';
import { useLeadGestures } from '../../../hooks/useLeadGestures';
import { useLeadSheet, type SheetLead } from '../../../hooks/useLeadSheet';
import { newMissionHref } from '../../../lib/bureau/newMission';
import { exchangesOf } from '../../../lib/crm/accountSheet';
import { leadFicheHref } from '../../../lib/crm/leadSheet';
import { useCanUseTelephony } from '../../../lib/telephonyAccess';
import type { CRMNote } from '../../../types/notes';
import { LegacyLayer } from '../../bureau/encreTone';
import { NoteDetailModal } from '../../notes/NoteDetailModal';
import { CallButton } from '../../telephony/CallButton';
import { AccountBriefSection } from '../account-record/AccountBriefSection';
import { EntityMissionsBlock } from '../EntityMissionsBlock';
import { ExchangesBlock } from './AccountSheetBlocks';
import { EncreSheet, ReReadError, SheetEmpty } from './EncreSheet';
import { LeadInvoicesBlock } from './LeadInvoicesBlock';
import { LeadOnboardingBlock } from './LeadOnboardingBlock';
import { LeadDealBlock, LeadIdentityBlocks, LeadInternalNotes } from './LeadSheetBlocks';
import { LeadSheetEdit } from './LeadSheetEdit';
import { LeadSheetHeader } from './LeadSheetHeader';

/**
 * An onboarding lead's working sheet, in Encre (PLAN-ENCRE-OUTILS O3 bis): where the client stands
 * in its onboarding and moving it through the back's gates, what Otto does about the client and
 * asking him, who it is and who to reach, its deal, its latest exchanges and Otto's briefs, its
 * manual invoices (the only place they are tied to a lead). « Modifier » edits it here. Opened from
 * every name of the app (`openLeadSidebar`).
 */
export function LeadSheet({ leadId, onClose }: { leadId: string | null; onClose: () => void }) {
  const sheet = useLeadSheet(leadId);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => setEditing(false), [leadId]);
  const lead = sheet.lead;

  return (
    <EncreSheet
      open={leadId !== null}
      label={lead ? `Fiche de ${lead.row.company_name || 'ce lead'}` : 'Fiche du lead'}
      focusKey={`${leadId}:${editing}`}
      dirty={editing && dirty}
      onClose={onClose}
    >
      {!lead ? (
        sheet.error ? (
          <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {sheet.error}</p>
        ) : sheet.missing ? (
          <SheetEmpty>Ce lead est introuvable : il a été supprimé, ou n'existe pas.</SheetEmpty>
        ) : (
          <div className="space-y-4 pt-2" aria-busy="true">
            <div className="h-14 w-2/3 animate-pulse rounded-2xl bg-desk-subtle" />
            <div className="h-24 animate-pulse rounded-2xl bg-desk-subtle" />
          </div>
        )
      ) : (
        <>
          {sheet.error && <ReReadError error={sheet.error} />}
          <LeadSheetBody lead={lead} sheet={sheet} editing={editing} setEditing={setEditing} onDirty={setDirty} onClose={onClose} />
        </>
      )}
    </EncreSheet>
  );
}

function LeadSheetBody({
  lead,
  sheet,
  editing,
  setEditing,
  onDirty,
  onClose,
}: {
  lead: SheetLead;
  sheet: ReturnType<typeof useLeadSheet>;
  editing: boolean;
  setEditing: (next: boolean) => void;
  onDirty: (dirty: boolean) => void;
  onClose: () => void;
}) {
  const { openAccountSidebar, openOpportunitySidebar, openAppointmentSidebar, openMeetingReportSidebar } = useSidebar();
  const canCall = useCanUseTelephony();
  const gestures = useLeadGestures(lead.row.id, sheet.reload);
  const [brief, setBrief] = useState<CRMNote | null>(null);
  const rows = { loading: !sheet.rowsReady && !sheet.rowsError, error: sheet.rowsError };
  const phone = lead.row.contact_phone?.trim() || null;
  const email = lead.row.contact_email?.trim() || null;
  const account = lead.account;

  return (
    <>
      <LeadSheetHeader
        lead={lead}
        call={canCall && phone ? <CallButton number={phone} accountId={account?.id ?? null} /> : null}
        email={email}
        newMissionHref={newMissionHref({ client: account?.id ?? null })}
        ficheHref={leadFicheHref(lead.row.id)}
        editing={editing}
        onOpenAccount={openAccountSidebar}
        onEdit={() => setEditing(true)}
        onNavigate={onClose}
      />
      {editing ? (
        <LeadSheetEdit
          lead={lead.row}
          saving={gestures.busy}
          error={gestures.error}
          onSave={gestures.save}
          onDone={() => setEditing(false)}
          onDirty={onDirty}
        />
      ) : (
        <>
          {gestures.error && (
            <p role="alert" className="mb-6 rounded-[10px] bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {gestures.error}
            </p>
          )}
          {/* Otto's missions are the client's: a lead is its account's (`get_entity_missions`). */}
          {account && <EntityMissionsBlock kind="account" entity={account} />}
          <LeadOnboardingBlock
            lead={lead}
            onboarding={sheet.onboarding}
            checklists={sheet.rows.checklists}
            busy={gestures.busy}
            onAdvance={(stage) => void gestures.advance(stage)}
            onReopen={gestures.reopen}
            onNavigate={onClose}
          />
          <LeadIdentityBlocks lead={lead} />
          <LeadDealBlock lead={lead} onOpen={openOpportunitySidebar} />
          <AccountBriefSection variant="encre" briefs={sheet.rows.briefs} onView={setBrief} />
          <ExchangesBlock
            exchanges={exchangesOf(sheet.rows.appointments, sheet.rows.notes, { reports: sheet.rows.reports })}
            state={rows}
            onOpenAppointment={openAppointmentSidebar}
            onOpenReport={openMeetingReportSidebar}
            noteHref={(id) => `/admin/notes?note=${encodeURIComponent(id)}`}
            onNavigate={onClose}
          />
          <LeadInvoicesBlock leadId={lead.row.id} customerId={lead.row.stripe_customer_id} />
          <LeadInternalNotes lead={lead} />
        </>
      )}
      {brief && (
        <LegacyLayer>
          <NoteDetailModal note={brief} onClose={() => setBrief(null)} readOnly />
        </LegacyLayer>
      )}
    </>
  );
}
